import type { IInput } from 'interfaces/IInput';
import type { IInputStep } from 'interfaces/IInputStep';
import {
  getInputTaskTypeLabel,
  InputTaskCategory,
  InputTaskType,
  type IInputTask,
} from 'interfaces/IInputTask';
import { formatDate } from 'pages/FarmsPage/Workspace/Tasks/taskFactory';
import { formatRate } from './nutritionFormat';

/**
 * One change to a plan's fertiliser program from the Nutrition lens
 * (OP-2523). An intent says what should change, not the task to send: it's
 * applied to the latest cached plan when it goes, so a retry or an undo
 * never puts back a stale copy of a task that changed in between.
 */
export type NutritionIntent =
  | {
      kind: 'setRate';
      inputId: string;
      taskId: string;
      stepId: string;
      rate: number;
    }
  | {
      kind: 'addStep';
      inputId: string;
      taskId: string;
      /** One or more products, added together in one save. */
      steps: IInputStep[];
      /** Where in the task's steps they go; the end when not set. */
      index?: number;
      /**
       * The task to create when it isn't on the plan — a new application, or
       * one put back by undo. Only used when the task is missing; its own
       * steps are replaced by these.
       */
      task?: IInputTask;
    }
  | {
      kind: 'removeStep';
      inputId: string;
      taskId: string;
      stepId: string;
      /** Delete the task when this was its last step, so nothing is left empty. */
      deleteIfEmpty: boolean;
    };

/** What's sent to the server for one intent: one whole task saved, or deleted. */
export type NutritionTaskOp =
  | { kind: 'upsert'; inputId: string; task: IInputTask }
  | { kind: 'delete'; inputId: string; taskId: string };

export interface ResolvedNutritionIntent {
  /** Null when there's nothing to do — already done, or the step or task has gone. */
  op: NutritionTaskOp | null;
  /** The intents that put it back, for undo. Empty with a null op. */
  inverse: NutritionIntent[];
}

const NOTHING: ResolvedNutritionIntent = { op: null, inverse: [] };

const findTask = (
  inputs: IInput[] | undefined,
  inputId: string,
  taskId: string
): IInputTask | undefined =>
  inputs
    ?.find((input) => input.id === inputId)
    ?.tasks?.find((task) => task.taskId === taskId);

const insertAt = <T>(items: T[], added: T[], index?: number): T[] => {
  const at =
    index == null ? items.length : Math.max(0, Math.min(index, items.length));
  return [...items.slice(0, at), ...added, ...items.slice(at)];
};

/** Takes each added product off again; the task goes with the last one when undo created it. */
const removeAdded = (
  intent: Extract<NutritionIntent, { kind: 'addStep' }>,
  steps: IInputStep[],
  deleteIfEmpty: boolean
): NutritionIntent[] =>
  steps.map((step) => ({
    kind: 'removeStep',
    inputId: intent.inputId,
    taskId: intent.taskId,
    stepId: step.stepId,
    deleteIfEmpty,
  }));

/**
 * Turns an intent into the save it needs against the plans as they are now,
 * and the intent that would undo it. Pure — the caller sends the op.
 */
export const resolveNutritionIntent = (
  inputs: IInput[] | undefined,
  intent: NutritionIntent
): ResolvedNutritionIntent => {
  const task = findTask(inputs, intent.inputId, intent.taskId);

  switch (intent.kind) {
    case 'setRate': {
      const step = task?.steps.find((s) => s.stepId === intent.stepId);
      if (!task || !step) return NOTHING;
      const previous = (step as { rate?: number | null }).rate ?? 0;
      if (previous === intent.rate) return NOTHING;
      return {
        op: {
          kind: 'upsert',
          inputId: intent.inputId,
          task: {
            ...task,
            steps: task.steps.map((s) =>
              s.stepId === intent.stepId
                ? ({ ...s, rate: intent.rate } as IInputStep)
                : s
            ),
          },
        },
        inverse: [{ ...intent, rate: previous }],
      };
    }

    case 'addStep': {
      if (task) {
        // Already there (a retry after it saved) — only add what's missing.
        const missing = intent.steps.filter(
          (step) => !task.steps.some((s) => s.stepId === step.stepId)
        );
        if (missing.length === 0) return NOTHING;
        return {
          op: {
            kind: 'upsert',
            inputId: intent.inputId,
            task: {
              ...task,
              steps: insertAt(task.steps, missing, intent.index),
            },
          },
          // The task was already there, so undo leaves it, even if empty.
          inverse: removeAdded(intent, missing, false),
        };
      }
      if (!intent.task || intent.steps.length === 0) return NOTHING;
      return {
        op: {
          kind: 'upsert',
          inputId: intent.inputId,
          task: { ...intent.task, steps: intent.steps },
        },
        inverse: removeAdded(intent, intent.steps, true),
      };
    }

    case 'removeStep': {
      const index = task?.steps.findIndex((s) => s.stepId === intent.stepId);
      if (!task || index == null || index < 0) return NOTHING;
      const step = task.steps[index];
      const remaining = task.steps.filter((s) => s.stepId !== intent.stepId);
      if (remaining.length === 0 && intent.deleteIfEmpty) {
        return {
          op: { kind: 'delete', inputId: intent.inputId, taskId: task.taskId },
          inverse: [
            {
              kind: 'addStep',
              inputId: intent.inputId,
              taskId: task.taskId,
              steps: [step],
              index,
              task: { ...task, steps: [] },
            },
          ],
        };
      }
      return {
        op: {
          kind: 'upsert',
          inputId: intent.inputId,
          task: { ...task, steps: remaining },
        },
        // No task snapshot: if someone deletes the task in between, undo
        // doesn't bring it back.
        inverse: [
          {
            kind: 'addStep',
            inputId: intent.inputId,
            taskId: task.taskId,
            steps: [step],
            index,
          },
        ],
      };
    }
  }
};

/** Task key used for "one save per task at a time". */
export const nutritionTaskKey = (inputId: string, taskId: string) =>
  `${inputId}:${taskId}`;

/* ═══════════════════════════════════════════════════════════════
   Where a product can go
   ═══════════════════════════════════════════════════════════════ */

/** The value the "add to" choice uses for a new application. */
export const NEW_APPLICATION_TARGET = 'new';

export interface NutritionTaskTarget {
  /** A taskId, or NEW_APPLICATION_TARGET. */
  value: string;
  /** Products first, then type and date, e.g. "Urea, MAP · Application · 12 June 2025" — for screen readers. */
  label: string;
  /** What tells applications apart, shown on its card; absent for a new application. */
  details?: {
    /** e.g. "July 03, 2026"; null when the task has no date. */
    date: string | null;
    /** e.g. "Tillering"; null when not set. */
    timing: string | null;
    /** e.g. "Application". */
    type: string;
    /** Every product with its rate, e.g. "Urea 100 kg/ha". */
    products: string[];
  };
}

// Fertiliser can only go on applications and sowings (isStepAllowedForTask).
const FERTILISER_TASK_TYPES = new Set<InputTaskType>([
  InputTaskType.Application,
  InputTaskType.CropEstablishment,
]);

const stepItem = (step: IInputStep): string =>
  (step as { item?: string | null }).item?.trim() ?? '';

/** The plan's planned applications and sowings, in date order (undated last). */
export const listFertiliserTasks = (input: IInput | undefined): IInputTask[] =>
  (input?.tasks ?? [])
    .filter(
      (task) =>
        task.category === InputTaskCategory.Plan &&
        FERTILISER_TASK_TYPES.has(task.taskType)
    )
    .sort((a, b) => {
      if (!a.endDate || !b.endDate) return a.endDate ? -1 : b.endDate ? 1 : 0;
      return a.endDate.localeCompare(b.endDate);
    });

/**
 * A task as the advisor recognises it: what's on it, then its type and date,
 * e.g. "Urea, MAP · Application · 12 June 2025".
 */
export const describeTask = (task: IInputTask, noProductsLabel: string) => {
  const items = task.steps.map(stepItem).filter(Boolean);
  const date = formatDate(task.endDate, task.endDateHasDay).trim();
  return [
    items.length > 0 ? items.join(', ') : noProductsLabel,
    getInputTaskTypeLabel(task.taskType),
    date,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Where a product can be added (or moved) on a plan: its planned
 * applications and sowings, then a new application. excludeTaskId leaves
 * out the task a product is moving from.
 */
export const listTaskTargets = (
  input: IInput | undefined,
  labels: { noProducts: string; newApplication: string },
  excludeTaskId?: string
): NutritionTaskTarget[] => [
  ...listFertiliserTasks(input)
    .filter((task) => task.taskId !== excludeTaskId)
    .map((task) => ({
      value: task.taskId,
      label: describeTask(task, labels.noProducts),
      details: {
        date: formatDate(task.endDate, task.endDateHasDay).trim() || null,
        timing: task.timing?.item?.trim() || null,
        type: getInputTaskTypeLabel(task.taskType),
        products: task.steps
          .map((step) => {
            const item = stepItem(step);
            const { rate, appUnit } = step as {
              rate?: number | null;
              appUnit?: { unit: string } | null;
            };
            if (!item) return '';
            return rate != null
              ? `${item} ${formatRate(rate, appUnit?.unit ?? null)}`
              : item;
          })
          .filter(Boolean),
      },
    })),
  { value: NEW_APPLICATION_TARGET, label: labels.newApplication },
];

/**
 * The choice to prefill: the plan's only application or sowing, or a new
 * application when it has none. Null when there's more than one to choose from.
 */
export const defaultTaskTarget = (
  targets: NutritionTaskTarget[]
): string | null => {
  const existing = targets.filter((t) => t.value !== NEW_APPLICATION_TARGET);
  if (existing.length === 0) return NEW_APPLICATION_TARGET;
  return existing.length === 1 ? existing[0].value : null;
};
